import { AUTONOMOUS_COMMUNITIES, EAutonomousCommunity } from '@constants';
import { Select } from 'antd';
import type { RefSelectProps } from 'antd/es/select';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { SelectOptionRenderFn, SelectOptionType } from '../BaseForm/types';

import { OptionsWrapper, ItemWrapper, ItemTextWrapper } from './AutonomousCommunitySelector.styles';

import { Icon } from '@/components/Icon';

type Props = {
  value?: EAutonomousCommunity | EAutonomousCommunity[];
  onChange?: (value: EAutonomousCommunity | EAutonomousCommunity[]) => void;
  allowClear?: boolean;
  mode?: 'multiple';
  disabled?: boolean;
  optionsRender?: SelectOptionRenderFn<SelectOptionType<EAutonomousCommunity>>;
  variant?: 'borderless' | 'outlined' | 'filled' | undefined;
};

export const AutonomousCommunitySelector: React.FC<Props> = ({
  value,
  onChange,
  allowClear = false,
  mode,
  disabled = false,
  optionsRender,
  variant = 'borderless'
}) => {
  const { t } = useTranslation('DefaultTranslations');
  const selectRef = useRef<RefSelectProps>(null);
  const options = useMemo(
    () =>
      AUTONOMOUS_COMMUNITIES.map(community => ({
        label: t(community),
        value: community
      })).sort((a, b) => a.label.localeCompare(b.label)),
    [AUTONOMOUS_COMMUNITIES]
  );

  const handleSelectOption = (option: EAutonomousCommunity) => {
    if (mode !== 'multiple') {
      if (value === option) {
        onChange?.(undefined as unknown as EAutonomousCommunity | EAutonomousCommunity[]);
      } else {
        onChange?.(option);
      }
      selectRef.current?.blur();
    } else {
      if (value && Array.isArray(value)) {
        if (value.includes(option)) {
          onChange?.(value.filter((val: EAutonomousCommunity) => val !== option));
        } else {
          onChange?.([...value, option]);
        }
      } else {
        onChange?.([option]);
      }
    }
  };

  return (
    <Select
      ref={selectRef}
      mode={mode}
      value={value}
      options={options}
      onChange={onChange}
      allowClear={allowClear}
      placeholder={t('AUTONOMOUS_COMMUNITY')}
      style={{ minWidth: '175px' }}
      disabled={disabled}
      variant={variant}
      optionRender={optionsRender}
      dropdownRender={() => {
        return (
          <OptionsWrapper>
            {options?.map(({ label, value: val }) => {
              const isSelected =
                value && Array.isArray(value) ? value.includes(val) : value === val;
              return (
                <ItemWrapper
                  key={val}
                  $isSelected={isSelected}
                  onClick={() => handleSelectOption(val)}>
                  <ItemTextWrapper>{label}</ItemTextWrapper>
                  {isSelected && <Icon i="Checkmark" color="green7" />}
                </ItemWrapper>
              );
            })}
          </OptionsWrapper>
        );
      }}
    />
  );
};
